// ─────────────────────────────────────────────────────────────────────────────
// Dashboard constants — Arabic-only labels, palette, date helpers.
// Anything visible to the user lives here so widgets stay free of literals.
// ─────────────────────────────────────────────────────────────────────────────

export const STRINGS = {
  dashboardTitle: 'لوحة التحكم الإحصائية',
  dashboardSubtitle: 'نظام إدارة الحجوزات والمنتجع — تحليلات متقدمة',

  filters: 'التصفية والتنقية',
  dateRange: 'نطاق التاريخ',
  serviceType: 'نوع الخدمة',
  paymentMethod: 'طريقة الدفع',
  startDate: 'تاريخ البدء',
  endDate: 'تاريخ الانتهاء',
  thisWeek: 'هذا الأسبوع',
  thisMonth: 'هذا الشهر',
  thisYear: 'هذه السنة',
  customRange: 'نطاق مخصص',
  fetchReport: 'عرض التقرير',
  loadingReport: 'جاري التحميل...',
  selectServicesPlaceholder: 'اختر الخدمات',
  selectPaymentsPlaceholder: 'اختر طرق الدفع',
  noOptions: 'لا توجد خيارات متاحة',

  totalReservations: 'إجمالي الحجوزات',
  totalVisitors: 'إجمالي الزوار',
  totalRevenue: 'إجمالي الإيرادات',
  avgRevenue: 'معدل إيراد الحجز',
  activatedBracelets: 'الأساور المفعّلة',
  popularService: 'الخدمة الأكثر طلباً',

  mainChartTitle: 'نظرة عامة على الحجوزات والإيرادات',
  mainChartSubtitle: 'مقارنة الزوار والإيرادات حسب نوع الخدمة',
  trendChartTitle: 'منحنى المبيعات والنشاط التفصيلي',
  paymentPieTitle: 'توزيع طرق الدفع',

  visitorsCount: 'عدد الأشخاص',
  reservationsCount: 'عدد الحجوزات',
  revenueAmount: 'الإيرادات',
  amountLabel: 'المبلغ',

  additionalAnalytics: 'إحصائيات وتحليلات إضافية',
  topSelling: 'الخدمة الأعلى مبيعاً',
  leastSelling: 'الخدمة الأقل مبيعاً',
  peakDay: 'يوم الذروة للحجز',
  avgDailyRev: 'متوسط الإيراد اليومي',

  currency: 'د.ل',
  transactions: 'عمليات',
  reservations: 'حجوزات',
  visitors: 'زوار',
  revenue: 'إيرادات',
  date: 'التاريخ',
  service: 'الخدمة',
  paymentDist: 'توزيع الدفع',
  perDay: 'يومياً',
  ofSales: 'من الإجمالي',

  zoomIn: 'تكبير',
  zoomOut: 'تصغير',
  resetZoom: 'إعادة ضبط',

  toggleLanguage: 'تبديل اللغة',
  toggleTheme: 'تبديل المظهر',

  // States
  loadingTitle: 'جاري تحميل البيانات...',
  loadingSubtitle: 'يرجى الانتظار قليلاً ريثما نجلب لك أحدث الإحصائيات',
  noDataTitle: 'لا توجد بيانات للعرض',
  noDataSubtitle: 'اختر نطاق التاريخ والخدمات وطرق الدفع ثم اضغط على عرض التقرير',
  emptyTitle: 'لا توجد نتائج تطابق التصفية',
  emptySubtitle: 'حاول توسيع نطاق التاريخ أو اختيار خدمات أو طرق دفع إضافية',
  errorTitle: 'تعذّر جلب البيانات',
  errorSubtitle: 'يرجى التحقق من الاتصال والمحاولة مرة أخرى',
  retry: 'إعادة المحاولة',

  // Validation
  selectAtLeastOneService: 'يرجى اختيار خدمة واحدة على الأقل',
  selectAtLeastOnePayment: 'يرجى اختيار طريقة دفع واحدة على الأقل',
  selectDateRange: 'يرجى تحديد نطاق التاريخ',
  warningTitle: 'تنبيه',
  errorToastTitle: 'خطأ',
  connectionErrorTitle: 'خطأ في الاتصال',
  connectionErrorDetail: 'تعذّر الاتصال بالخادم، يرجى المحاولة مرة أخرى',

  // Tooltip
  visitorsByService: 'الزوار حسب الخدمة',
  summary: 'الملخص',
  paymentDistribution: 'توزيع الدفع',

  // Navigation
  previous: 'السابق',
  next: 'التالي',
  today: 'اليوم',
  currentPeriod: 'الفترة الحالية',

  // Service strip
  allServices: 'جميع الخدمات',
  servicesFilter: 'تصفية الخدمات',
  noServices: 'لا توجد خدمات لعرضها',
};

// Single ordered palette — components pick by index so colours stay stable
// across service / payment lists of any length.
export const CHART_PALETTE = [
  '#3b82f6',
  '#10b981',
  '#f59e0b',
  '#8b5cf6',
  '#ec4899',
  '#06b6d4',
  '#f43f5e',
  '#84cc16',
  '#a855f7',
  '#14b8a6',
];

export const pickColor = (index) => CHART_PALETTE[index % CHART_PALETTE.length];

export const AR_MONTHS = [
  'يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو',
  'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر',
];

// moment().day() returns 0 = Sunday … 6 = Saturday.
export const AR_DAYS = [
  'الأحد', 'الإثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت',
];

// "3 - الثلاثاء" — used by all per-day axis labels so weekly + monthly views
// share the same readable format.
export const formatDayLabel = (momentDate) =>
  `${momentDate.date()} - ${AR_DAYS[momentDate.day()]}`;

export const DATE_RANGE_OPTIONS = [
  { value: 'week', label: STRINGS.thisWeek },
  { value: 'month', label: STRINGS.thisMonth },
  { value: 'year', label: STRINGS.thisYear },
  { value: 'custom', label: STRINGS.customRange },
];

export const formatNumber = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '0';
  return n.toLocaleString('ar-LY');
};

export const formatCurrency = (value) =>
  `${formatNumber(Math.round(Number(value) || 0))} ${STRINGS.currency}`;

export const formatCompact = (value) => {
  const n = Number(value) || 0;
  if (Math.abs(n) >= 1000) {
    const k = n / 1000;
    const trimmed = k.toFixed(1).replace('.0', '');
    return `${trimmed}K`;
  }
  return String(Math.round(n));
};
